import React, { useCallback, useState } from 'react';
import { FileDown, ArrowLeft, Globe } from 'lucide-react';
import { rondeSeedT } from '../rondeSeedTranslations';

const LOGO = 'https://customer-assets.emergentagent.com/job_foodtech-finplan/artifacts/5cp42cda_image.png';

function SectionBlock({ id, children }) {
  return (
    <section id={id} className="mb-16 scroll-mt-8" data-testid={`section-${id}`}>
      {children}
    </section>
  );
}

function SectionHeading({ title, subtitle }) {
  return (
    <div className="mb-6 pb-4 border-b border-gray-700">
      <h2 className="text-xl sm:text-2xl font-bold text-white tracking-tight">{title}</h2>
      {subtitle && <p className="text-sm text-gray-400 mt-1 italic">{subtitle}</p>}
    </div>
  );
}

function SubHeading({ children }) {
  return <h3 className="text-base font-semibold text-lime-400 mt-6 mb-3">{children}</h3>;
}

function Paragraph({ children }) {
  return <p className="text-sm text-gray-300 leading-relaxed mb-4">{children}</p>;
}

function BulletList({ items }) {
  return (
    <ul className="space-y-2 mb-4 ml-1">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
          <span className="text-lime-500 mt-0.5 flex-shrink-0">-</span>
          <span className="leading-relaxed">{item}</span>
        </li>
      ))}
    </ul>
  );
}

function RiskItem({ title, desc }) {
  return (
    <div className="mb-3">
      <p className="text-sm text-gray-300 leading-relaxed">
        <span className="text-orange-400 font-medium">{title} : </span>{desc}
      </p>
    </div>
  );
}

function DeRiskItem({ text }) {
  return (
    <li className="flex items-start gap-2 text-sm text-gray-300">
      <span className="text-lime-500 mt-0.5 flex-shrink-0">+</span>
      <span className="leading-relaxed">{text}</span>
    </li>
  );
}

function FundCategory({ title, desc }) {
  return (
    <div className="mb-4">
      <p className="text-sm text-gray-300 leading-relaxed">
        <span className="text-white font-medium">{title}.</span> {desc}
      </p>
    </div>
  );
}

function LeadPoint({ title, desc }) {
  return (
    <div className="mb-5 pl-4 border-l-2 border-lime-500/30">
      <p className="text-sm font-semibold text-white mb-1">{title}</p>
      <p className="text-sm text-gray-300 leading-relaxed">{desc}</p>
    </div>
  );
}

export default function RondeSeedPage() {
  const [language, setLanguage] = useState('fr');
  const t = rondeSeedT[language];
  const toggleLanguage = () => setLanguage(l => l === 'fr' ? 'en' : 'fr');
  const handleDownload = useCallback(() => { window.print(); }, []);

  return (
    <div className="min-h-screen bg-gray-950 text-gray-200">
      {/* Print styles */}
      <style>{`
        @media print {
          .rs-toolbar { display: none !important; }
          body, html { margin: 0 !important; padding: 0 !important; background: white !important; }
          .min-h-screen { min-height: auto !important; background: white !important; }
          .rs-container { max-width: 100% !important; padding: 20px !important; }
          * { color: #111 !important; border-color: #ccc !important; }
          h2 { color: #111 !important; }
          h3 { color: #333 !important; }
          .text-lime-400, .text-lime-500 { color: #333 !important; }
          .text-orange-400 { color: #555 !important; }
          section { page-break-inside: avoid; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; font-size: 11pt; }
        }
      `}</style>

      {/* Top toolbar */}
      <div className="rs-toolbar sticky top-0 z-50 bg-gray-950/95 backdrop-blur-md border-b border-gray-800">
        <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm" data-testid="rs-back-btn">
              <ArrowLeft className="h-4 w-4" />
              {t.backToSite}
            </a>
            <div className="h-5 w-px bg-gray-700" />
            <img src={LOGO} alt="EcoLunch" className="h-7 w-auto" />
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-gray-300 border border-gray-700 rounded-lg transition-colors text-xs font-medium"
              data-testid="rs-lang-toggle"
            >
              <Globe className="h-3.5 w-3.5" />
              {language === 'fr' ? 'EN' : 'FR'}
            </button>
            <button
              onClick={handleDownload}
              className="flex items-center gap-2 px-4 py-1.5 bg-white text-gray-900 rounded-lg text-xs font-semibold hover:bg-gray-100 transition-colors"
              data-testid="rs-download-pdf-btn"
            >
              <FileDown className="h-3.5 w-3.5" />
              {t.downloadPdf}
            </button>
          </div>
        </div>
      </div>

      {/* Document body */}
      <div className="rs-container max-w-4xl mx-auto px-6 py-12">
        {/* Document header */}
        <div className="mb-16 text-center">
          <img src={LOGO} alt="EcoLunch" className="h-12 w-auto mx-auto mb-6" />
          <h1 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-2" data-testid="rs-page-title">{t.pageTitle}</h1>
          <p className="text-sm text-gray-500">{t.pageSubtitle}</p>
          <div className="w-20 h-0.5 bg-gray-700 mx-auto mt-6" />
        </div>

        {/* Table of Contents */}
        <nav className="mb-16 p-6 bg-gray-900/50 rounded-xl border border-gray-800" data-testid="rs-toc">
          <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">{t.tocTitle}</h2>
          <ol className="space-y-2">
            {[
              { id: 's1', label: t.s1Title },
              { id: 's2', label: t.s2Title },
              { id: 's3', label: t.s3Title },
              { id: 's4', label: t.s4Title },
              { id: 's5', label: t.s5Title },
              { id: 's6', label: t.s6Title },
              { id: 's7', label: t.s7Title },
              { id: 's8', label: t.s8Title },
              { id: 's9', label: t.s9Title },
              { id: 's10', label: t.s10Title },
            ].map((item) => (
              <li key={item.id}>
                <a
                  href={`#${item.id}`}
                  className="text-sm text-gray-400 hover:text-white transition-colors duration-200 block py-1 border-b border-gray-800/50 hover:border-gray-600"
                  data-testid={`toc-${item.id}`}
                >
                  {item.label}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        {/* Section 1 - Investment Snapshot */}
        <SectionBlock id="s1">
          <SectionHeading title={t.s1Title} subtitle={t.s1Subtitle} />
          <Paragraph>{t.s1p1}</Paragraph>
          <Paragraph>{t.s1p2}</Paragraph>
          <Paragraph>{t.s1p3}</Paragraph>
          <Paragraph>{t.s1p4}</Paragraph>
          <Paragraph>{t.s1p5}</Paragraph>
          <Paragraph>{t.s1p6}</Paragraph>
        </SectionBlock>

        {/* Section 2 - Round Structure */}
        <SectionBlock id="s2">
          <SectionHeading title={t.s2Title} />
          <Paragraph>{t.s2p1}</Paragraph>

          <SubHeading>{t.s2StructureTitle}</SubHeading>
          <BulletList items={[t.s2Lead, t.s2Followers, t.s2Min, t.s2Target, t.s2Max]} />

          <SubHeading>{t.s2TermsTitle}</SubHeading>
          <BulletList items={[t.s2Term1, t.s2Term2, t.s2Term3, t.s2Term4, t.s2Term5]} />

          <SubHeading>{t.s2LogicTitle}</SubHeading>
          <Paragraph>{t.s2LogicP}</Paragraph>
        </SectionBlock>

        {/* Section 3 - Traction */}
        <SectionBlock id="s3">
          <SectionHeading title={t.s3Title} />
          <Paragraph>{t.s3Intro}</Paragraph>

          <SubHeading>{t.s3PilotTitle}</SubHeading>
          <Paragraph>{t.s3PilotIntro}</Paragraph>
          <BulletList items={[t.s3Pilot1, t.s3Pilot2, t.s3Pilot3, t.s3Pilot4, t.s3Pilot5, t.s3Pilot6, t.s3Pilot7]} />
          <Paragraph>{t.s3PilotConclusion}</Paragraph>

          <SubHeading>{t.s3T2Title}</SubHeading>
          <BulletList items={[t.s3T2_1, t.s3T2_2, t.s3T2_3]} />
          <Paragraph>{t.s3T2Conclusion}</Paragraph>

          <SubHeading>{t.s3PrudenceTitle}</SubHeading>
          <Paragraph>{t.s3PrudenceP}</Paragraph>
        </SectionBlock>

        {/* Section 4 - Unit Economics */}
        <SectionBlock id="s4">
          <SectionHeading title={t.s4Title} subtitle={t.s4Subtitle} />
          <Paragraph>{t.s4Intro}</Paragraph>

          <SubHeading>{t.s4DistinctionTitle}</SubHeading>
          <BulletList items={[t.s4GMV, t.s4RevBrut, t.s4RevNet]} />

          <SubHeading>{t.s4TarifTitle}</SubHeading>
          <div className="mb-4 p-4 bg-gray-900/60 rounded-lg border border-gray-800">
            <p className="text-xs font-semibold text-white mb-2 uppercase tracking-wider">{t.s4CardTitle}</p>
            <BulletList items={[t.s4CardCharge, t.s4CardPay, t.s4CardNote]} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div className="p-4 bg-gray-900/60 rounded-lg border border-gray-800">
              <p className="text-xs font-semibold text-white mb-2 uppercase tracking-wider">{t.s4InteracTitle}</p>
              <p className="text-sm text-gray-300">{t.s4InteracMargin}</p>
            </div>
            <div className="p-4 bg-gray-900/60 rounded-lg border border-gray-800">
              <p className="text-xs font-semibold text-white mb-2 uppercase tracking-wider">{t.s4SezzleTitle}</p>
              <p className="text-sm text-gray-300">{t.s4SezzleMargin}</p>
            </div>
          </div>

          <SubHeading>{t.s4SaaSTitle}</SubHeading>
          <Paragraph>{t.s4SaaSP}</Paragraph>
        </SectionBlock>

        {/* Section 5 - Break-Even */}
        <SectionBlock id="s5">
          <SectionHeading title={t.s5Title} subtitle={t.s5Subtitle} />
          <Paragraph>{t.s5Intro}</Paragraph>

          <SubHeading>{t.s5HypoTitle}</SubHeading>
          <BulletList items={[t.s5Hypo1, t.s5Hypo2, t.s5Hypo3]} />

          <SubHeading>{t.s5SeuilTitle}</SubHeading>
          <Paragraph>{t.s5SeuilP}</Paragraph>

          <SubHeading>{t.s5ParticipationTitle}</SubHeading>
          <Paragraph>{t.s5ParticipationP}</Paragraph>

          <SubHeading>{t.s5ContextTitle}</SubHeading>
          <Paragraph>{t.s5ContextP}</Paragraph>
        </SectionBlock>

        {/* Section 6 - Pipeline Phase 1 */}
        <SectionBlock id="s6">
          <SectionHeading title={t.s6Title} />

          <SubHeading>{t.s6BassinTitle}</SubHeading>
          <BulletList items={[t.s6Bassin1, t.s6Bassin2, t.s6Bassin3]} />
          <Paragraph>{t.s6BassinNote}</Paragraph>

          <SubHeading>{t.s6TractionTitle}</SubHeading>
          <BulletList items={[t.s6Traction1, t.s6Traction2, t.s6Traction3, t.s6Traction4]} />

          <SubHeading>{t.s6HypoTitle}</SubHeading>
          <BulletList items={[t.s6Hypo1, t.s6Hypo2, t.s6Hypo3, t.s6Hypo4]} />

          <SubHeading>{t.s6DeployTitle}</SubHeading>
          <div className="space-y-3 mb-4">
            <div className="pl-4 border-l-2 border-gray-700">
              <p className="text-sm font-medium text-white mb-1">1. {t.s6Deploy1Title}</p>
              <p className="text-sm text-gray-300 leading-relaxed">{t.s6Deploy1}</p>
            </div>
            <div className="pl-4 border-l-2 border-gray-700">
              <p className="text-sm font-medium text-white mb-1">2. {t.s6Deploy2Title}</p>
              <p className="text-sm text-gray-300 leading-relaxed">{t.s6Deploy2}</p>
            </div>
            <div className="pl-4 border-l-2 border-gray-700">
              <p className="text-sm font-medium text-white mb-1">3. {t.s6Deploy3Title}</p>
              <p className="text-sm text-gray-300 leading-relaxed">{t.s6Deploy3}</p>
            </div>
          </div>
          <Paragraph>{t.s6Conclusion}</Paragraph>
        </SectionBlock>

        {/* Section 7 - Investment Thesis */}
        <SectionBlock id="s7">
          <SectionHeading title={t.s7Title} subtitle={t.s7Subtitle} />
          <Paragraph>{t.s7Intro}</Paragraph>

          <SubHeading>{t.s7ReadyTitle}</SubHeading>
          <BulletList items={[t.s7Ready1, t.s7Ready2, t.s7Ready3, t.s7Ready4, t.s7Ready5, t.s7Ready6, t.s7Ready7]} />

          <SubHeading>{t.s7WhyNowTitle}</SubHeading>
          <Paragraph>{t.s7WhyNowP}</Paragraph>

          <SubHeading>{t.s7FundsTitle}</SubHeading>
          <BulletList items={[t.s7Fund1, t.s7Fund2, t.s7Fund3, t.s7Fund4, t.s7Fund5]} />
          <div className="p-4 bg-gray-900/60 rounded-lg border border-gray-800 mt-4">
            <p className="text-sm text-gray-300 leading-relaxed italic">{t.s7Urgency}</p>
          </div>
        </SectionBlock>

        {/* Section 8 - Use of Funds */}
        <SectionBlock id="s8">
          <SectionHeading title={t.s8Title} subtitle={t.s8Subtitle} />
          <Paragraph>{t.s8Intro}</Paragraph>
          <FundCategory title={t.s8Cat1Title} desc={t.s8Cat1} />
          <FundCategory title={t.s8Cat2Title} desc={t.s8Cat2} />
          <FundCategory title={t.s8Cat3Title} desc={t.s8Cat3} />
          <FundCategory title={t.s8Cat4Title} desc={t.s8Cat4} />
          <FundCategory title={t.s8Cat5Title} desc={t.s8Cat5} />
          <div className="mt-4 p-4 bg-gray-900/40 rounded-lg border border-gray-800">
            <p className="text-xs text-gray-400 leading-relaxed italic">{t.s8Note}</p>
          </div>
        </SectionBlock>

        {/* Section 9 - Risks */}
        <SectionBlock id="s9">
          <SectionHeading title={t.s9Title} />

          <SubHeading>{t.s9RiskTitle}</SubHeading>
          <Paragraph>{t.s9RiskIntro}</Paragraph>
          <RiskItem title={t.s9Risk1Title} desc={t.s9Risk1} />
          <RiskItem title={t.s9Risk2Title} desc={t.s9Risk2} />
          <RiskItem title={t.s9Risk3Title} desc={t.s9Risk3} />
          <RiskItem title={t.s9Risk4Title} desc={t.s9Risk4} />
          <RiskItem title={t.s9Risk5Title} desc={t.s9Risk5} />

          <SubHeading>{t.s9DeRiskTitle}</SubHeading>
          <ul className="space-y-2 mb-4 ml-1">
            <DeRiskItem text={t.s9DeRisk1} />
            <DeRiskItem text={t.s9DeRisk2} />
            <DeRiskItem text={t.s9DeRisk3} />
            <DeRiskItem text={t.s9DeRisk4} />
            <DeRiskItem text={t.s9DeRisk5} />
            <DeRiskItem text={t.s9DeRisk6} />
            <DeRiskItem text={t.s9DeRisk7} />
          </ul>
        </SectionBlock>

        {/* Section 10 - Lead Investor Thesis */}
        <SectionBlock id="s10">
          <SectionHeading title={t.s10Title} subtitle={t.s10Subtitle} />
          <Paragraph>{t.s10Intro}</Paragraph>
          <LeadPoint title={t.s10Point1Title} desc={t.s10Point1} />
          <LeadPoint title={t.s10Point2Title} desc={t.s10Point2} />
          <LeadPoint title={t.s10Point3Title} desc={t.s10Point3} />
          <LeadPoint title={t.s10Point4Title} desc={t.s10Point4} />
          <LeadPoint title={t.s10Point5Title} desc={t.s10Point5} />
          <LeadPoint title={t.s10Point6Title} desc={t.s10Point6} />
          <LeadPoint title={t.s10Point7Title} desc={t.s10Point7} />
        </SectionBlock>

        {/* Footer - Points to confirm */}
        <div className="mt-16 pt-8 border-t border-gray-800">
          <h2 className="text-lg font-bold text-gray-400 mb-4">{t.confirmTitle}</h2>
          <ul className="space-y-2">
            {[t.confirm1, t.confirm2, t.confirm3, t.confirm4, t.confirm5, t.confirm6].map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-gray-500">
                <span className="text-gray-600 mt-0.5 flex-shrink-0">-</span>
                <span className="leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Document footer */}
        <div className="mt-16 pt-6 border-t border-gray-800 text-center">
          <img src={LOGO} alt="EcoLunch" className="h-8 w-auto mx-auto mb-3 opacity-50" />
          <p className="text-xs text-gray-600">EcoTech Systeme — Confidentiel</p>
        </div>
      </div>
    </div>
  );
}
